import { ArrowBackIos, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, IconButton, List, ListItemButton, ListItemText, Stack, Step, StepLabel, Stepper, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import CopyToClipboard from "src/components/shared/CopyToClipboard";
import ProcessingBadge from "src/components/shared/ProcessingBagde";
import appConfig from "src/config/env";
import { theme } from "src/config/theme";
import { ORDER_STATUS_TRANSLATE } from "src/constant";
import convertCurrency, { convertIntMoney } from "src/helper/convertCurrency";
import useRedirect from "src/helper/direct";
import { PATH } from "src/helper/path";
import useOrderDetail from "src/hooks/orders/useOrderDetail";
import useOrderLogDetail from "src/hooks/orders/useOrderLogDetail";
import useQueryParams from "src/hooks/useQueryParams";

export default function OrderDetail({ id }) {
  const { data: orderDetail } = useOrderDetail(id);
  const direct = useRedirect();
  const status = orderDetail?.data?.trackingStatus ?? orderDetail?.data?.status;

  const onDirect = () => {
    direct(PATH.ORDER);
  };

  return (
    <Stack
      overflow="auto"
      height="100vh"
    >
      <Stack>
        <Typography
          fontSize="18px"
          fontWeight="bold"
        >
          Quản lý đơn hàng
        </Typography>
        <Typography
          variant="body2"
          color="inherit"
        >
          Chi tiết đơn hàng
        </Typography>
      </Stack>
      {/* CONTENT */}
      <Stack
        border="1px solid var(--grey)"
        direction="column"
        flexGrow={1}
        borderRadius="6px"
        mt={1}
      >
        <Stack
          width="100%"
          borderBottom="1px solid var(--grey)"
        >
          <Box
            p={2}
            display="flex"
            alignItems="flex-start"
            gap={1}
          >
            <IconButton
              width="24px"
              height="24px"
              variant="contained"
              onClick={onDirect}
              sx={{
                background: "var(--black) !important",
                color: theme.palette.common.white,
              }}
            >
              <ArrowBackIos sx={{ fontSize: "12px" }} />
            </IconButton>
            <Box>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Typography fontWeight="bold">Chi tiết đơn hàng</Typography>
                <ProcessingBadge
                  value={ORDER_STATUS_TRANSLATE[status] ?? "-"}
                  asset={{
                    color: status || "Pending",
                    text: status || "Pending",
                  }}
                />
              </Box>
              <CopyToClipboard
                isCenter={false}
                value={orderDetail?.data?.code}
              />
            </Box>
          </Box>
        </Stack>
        <Stack
          direction="row"
          flexGrow={1}
        >
          {/* INFO */}
          <Stack
            flex={1}
            borderRight="1px solid var(--grey)"
          >
            <Info orderDetail={orderDetail} />
          </Stack>
          {/* TRACKING */}
          <Stack flex={1}>
            <Box
              borderBottom="1px solid var(--grey)"
              paddingBottom={3}
              maxHeight="350px"
              overflow="auto"
            >
              <Box p={2}>
                <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>Lịch trình:</Typography>
                <Tracking id={orderDetail?.data?._id} />
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
              p={2}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>Chi tiết vận chuyển:</Typography>
              <Box>
                <Typography sx={{ fontSize: "12px" }}>
                  <strong>Ngày lấy hàng: </strong> {dayjs(orderDetail?.data?.pickupDate).format("DD-MM-YYYY HH:mm")}
                </Typography>
                <Typography sx={{ fontSize: "12px" }}>
                  <strong>Lấy hàng tại: </strong> {orderDetail?.data?.pickUpAddress}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "12px" }}>
                  <strong>Lý do: </strong>
                </Typography>
                <Typography>{orderDetail?.data?.returnReason ?? "N/A"}</Typography>
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

const Info = ({ orderDetail }) => {
  const detailId = orderDetail?.data?.currentSubOrder?._id || orderDetail?.data?.rootOrder?._id;
  const { data: detailData } = useOrderDetail(detailId);
  const [collapse, setCollapse] = useState({ 1: false, 2: false });

  const handleExtractOrderData = (data) => ({
    code: data?.code,
    shippingProvider: data?.shippingProvider,
    paymentMethod: data?.paymentMethod,
    customer: data?.customer,
    total: data?.total,
    shippingFee: data?.shippingFee,
    note: data?.note,
    orderDetails: data?.orderDetails,
    recipient: data?.recipient,
    sales: data?.sales,
    type: data?.type,
  });

  useEffect(() => {
    if (!orderDetail?.data?.rootOrder) {
      setCollapse({ 1: false, 2: true });
      return;
    }
    setCollapse({ 1: true, 2: false });
  }, [orderDetail?.data?.rootOrder]);

  const { rootOrderData, subOrderData } = useMemo(() => {
    const isSubOrder = !!orderDetail?.data?.rootOrder?._id;
    const rootSource = isSubOrder ? detailData?.data : orderDetail?.data;
    const subSource = isSubOrder ? orderDetail?.data : detailData?.data;

    return {
      rootOrderData: handleExtractOrderData(rootSource),
      subOrderData: handleExtractOrderData(subSource),
    };
  }, [orderDetail?.data, detailData?.data]);

  const subOrderLabel = useMemo(() => {
    switch (subOrderData?.type) {
      case "Return":
        return "Thông tin đơn Trả hàng / Hoàn tiền";
      case "Exchange":
        return "Thông tin đơn Đổi hàng";
      default:
        return "Thông tin đơn hàng con";
    }
  }, [subOrderData?.type]);

  return (
    <>
      <Stack flexGrow={1}>
        <Stack borderBottom="1px solid var(--grey)">
          <Box p={2}>
            <Typography fontWeight="bold">Thông tin khách hàng</Typography>
            <Typography variant="body2">
              {orderDetail?.data?.customer?.fullName} - {orderDetail?.data?.customer?.phone}
            </Typography>
            <Typography variant="body2">{orderDetail?.data?.recipient?.detailAddress}</Typography>
            <Typography
              variant="body2"
              mt={1}
            >
              <strong>Nhân viên KD phụ trách: </strong>
              {orderDetail?.data?.sales?.fullName}
            </Typography>
          </Box>
        </Stack>
        <Stack direction={orderDetail?.data?.currentSubOrder?._id ? "column-reverse" : "column"}>
          <Box>
            {(orderDetail?.data?.rootOrder || orderDetail?.data?.currentSubOrder) && (
              <>
                <ListItemButton
                  sx={{ background: "var(--grey)" }}
                  onClick={() => setCollapse({ 1: !collapse[1], 2: false })}
                >
                  <ListItemText primary={subOrderLabel} />
                  {collapse[1] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse
                  in={collapse[1]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List
                    component="div"
                    disablePadding
                  >
                    <ListItemButton>
                      <ListItemText sx={{ p: 0 }}>
                        <Stack borderBottom="1px solid var(--grey)">
                          <Box
                            display="flex"
                            flexDirection="column"
                            gap={1}
                          >
                            <Typography fontWeight="bold">Thông tin đơn hàng</Typography>
                            <Typography
                              variant="body2"
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                            >
                              <strong>Mã đơn hàng: </strong>
                              <CopyToClipboard
                                isCenter={false}
                                value={subOrderData?.code}
                              />
                            </Typography>
                            <Typography variant="body2">
                              <strong>Đơn vị vận chuyển: </strong>
                              {subOrderData?.shippingProvider?.name}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Hình thức thanh toán: </strong>
                              {subOrderData?.paymentMethod?.name}
                            </Typography>
                            <Typography variant="body2">
                              <strong>VAT: </strong>
                              {subOrderData?.customer?.customVat ?? "N/a"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Thành tiền sau VAT: </strong>
                              {convertIntMoney(subOrderData?.total)} vnđ
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                            >
                              ({convertCurrency(subOrderData?.total)} đồng)
                            </Typography>
                            <Typography
                              p={1}
                              sx={{ background: "var(--grey)", fontSize: "10px" }}
                            >
                              <strong>Ghi chú từ đơn hàng: </strong> {subOrderData?.note}
                            </Typography>
                            <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>Danh sách sản phẩm</Typography>
                            <Stack
                              maxHeight="200px"
                              height="100%"
                              overflow="auto"
                            >
                              {subOrderData?.orderDetails?.map((v) => {
                                return (
                                  <Item
                                    key={v?._id}
                                    item={v}
                                  />
                                );
                              })}
                            </Stack>
                          </Box>
                        </Stack>
                      </ListItemText>
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}
          </Box>
          <Box>
            <ListItemButton
              sx={{ background: "var(--grey)" }}
              onClick={() => setCollapse({ 1: false, 2: !collapse[2] })}
            >
              <ListItemText primary="Thông tin đơn hàng gốc" />
              {collapse[2] ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse
              in={collapse[2]}
              timeout="auto"
              unmountOnExit
            >
              <List
                component="div"
                disablePadding
              >
                <ListItemButton>
                  <ListItemText>
                    <Stack borderBottom="1px solid var(--grey)">
                      <Box
                        display="flex"
                        flexDirection="column"
                        gap={1}
                      >
                        <Typography fontWeight="bold">Thông tin đơn hàng</Typography>
                        <Typography
                          variant="body2"
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <strong>Mã đơn hàng: </strong>
                          <CopyToClipboard
                            isCenter={false}
                            value={rootOrderData?.code}
                          />
                        </Typography>
                        <Typography variant="body2">
                          <strong>Đơn vị vận chuyển: </strong>
                          {rootOrderData?.shippingProvider?.name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Hình thức thanh toán: </strong>
                          {rootOrderData?.paymentMethod?.name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>VAT: </strong>
                          {rootOrderData?.customer?.customVat ?? "N/a"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Thành tiền sau VAT: </strong>
                          {convertIntMoney(rootOrderData?.total)} vnđ
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                        >
                          ({convertCurrency(rootOrderData?.total)} đồng)
                        </Typography>
                        <Typography
                          p={1}
                          sx={{ background: "var(--grey)", fontSize: "10px" }}
                        >
                          <strong>Ghi chú từ đơn hàng: </strong> {rootOrderData?.note}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>Danh sách sản phẩm</Typography>
                        <Stack
                          maxHeight="200px"
                          height="100%"
                          overflow="auto"
                        >
                          {rootOrderData?.orderDetails?.map((v) => {
                            return (
                              <Item
                                key={v?._id}
                                item={v}
                              />
                            );
                          })}
                        </Stack>
                      </Box>
                    </Stack>
                  </ListItemText>
                </ListItemButton>
              </List>
            </Collapse>
          </Box>
        </Stack>
      </Stack>
      <Stack borderTop="1px solid var(--grey)">
        <Box
          p={2}
          display="flex"
        >
          <Box
            width="65%"
            display="flex"
            flexDirection="column"
            textAlign="right"
          >
            <Typography
              variant="caption"
              fontWeight="bold"
            >
              Tổng
            </Typography>
            <Typography
              variant="caption"
              fontWeight="bold"
            >
              Phí ship
            </Typography>
            <Typography
              variant="caption"
              fontWeight="bold"
            >
              Thành tiền
            </Typography>
          </Box>
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            textAlign="right"
          >
            <Typography
              variant="caption"
              fontWeight="bold"
            >
              {convertIntMoney(orderDetail?.data?.total ?? 0)}đ
            </Typography>
            <Typography variant="caption">{convertIntMoney(orderDetail?.data?.shippingFee ?? 0)}đ</Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
            >
              {convertIntMoney(orderDetail?.data?.total + orderDetail?.data?.shippingFee)}đ
            </Typography>
          </Box>
        </Box>
      </Stack>
    </>
  );
};

const Item = ({ item }) => {
  const finalPrice = item?.price - (item?.price * item?.discount) / 100;

  return (
    <Box pb={1.5}>
      <Box
        display="flex"
        gap={1}
        justifyContent="space-between"
        width="100%"
      >
        <Box
          display="flex"
          gap={1}
          width="60%"
        >
          <Box
            sx={{
              width: "46px",
              height: "46px",
              border: "1px solid #cccccc",
              borderRadius: "8px",
              backgroundColor: "white",
              background: !!item?.productModel?.image?.src ? `url(${appConfig.API_FILE}/${item?.productModel?.image?.src})` : "var(--grey)",
              backgroundSize: "100%",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          />

          <Box
            display="flex"
            flexDirection="column"
          >
            <Typography variant="caption">{item?.productName ?? "-"}</Typography>
            <Typography variant="caption">{item?.productModel?.name}</Typography>
          </Box>
        </Box>

        <Box
          textAlign="center"
          width="20%"
        >
          <Typography
            fontWeight="bold"
            variant="caption"
          >
            {item?.qty ?? 0}
          </Typography>
          <Typography fontSize="8px">Sản phẩm</Typography>
        </Box>
        <Box
          width="20%"
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
        >
          <Typography
            variant="caption"
            fontWeight="bold"
          >
            {convertIntMoney(finalPrice)}đ
          </Typography>
          <Box
            display="flex"
            gap={1}
          >
            <Typography sx={{ fontSize: "8px", textDecoration: "line-through" }}>{convertIntMoney(item?.price)}đ</Typography>
            <Typography sx={{ fontSize: "8px" }}>CK {item?.discount ?? 0}%</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const Tracking = ({ id }) => {
  const [option] = useQueryParams();
  const { data: log } = useOrderLogDetail(id, {
    warehouse: option?.warehouseId,
    sortField: "createdAt",
    sortOrder: "desc",
  });

  return (
    <Stepper
      activeStep={0}
      orientation="vertical"
      alternativeLabel={false}
      sx={{
        mt: 2,
        "& .MuiStep-root": {
          position: "relative",
          paddingLeft: "16px",
          paddingBottom: 1.5,

          "&::before": {
            content: '""',
            position: "absolute",
            left: "27.5px",
            top: "11px",
            width: "1px",
            height: "100%",
            backgroundColor: "#d9d9d9",
          },
        },

        // hide default MUI connector
        ".MuiStepConnector-line": { display: "none !important" },

        // control icon area
        ".MuiStepLabel-iconContainer": {
          width: "24px",
          height: "24px",
          display: "flex",
          justifyContent: "center",
        },

        "& .MuiStepIcon-root": {
          color: "#d9d9d9",
          width: "6px",
          height: "6px",
          mt: "5px",
          borderRadius: "50%",
          "&.Mui-active": {
            color: "var(--confirm-txt) !important",
            width: "10px",
            height: "10px",
            zIndex: 1,
          },
          "& text": {
            display: "none",
          },
        },
      }}
    >
      {log?.data?.map((label, index) => (
        <Step key={label?.statusTextVi}>
          <StepLabel sx={{ p: 0, alignItems: "flex-start" }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "bold",
                color: index === 0 ? "var(--confirm-txt)" : "",
              }}
            >
              {label?.statusTextVi}
            </Typography>

            <Typography sx={{ fontSize: "10px" }}>{label?.subDetail}</Typography>

            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: !label?.statusTextVi ? "bold" : "",
                fontStyle: "italic",
                color: !label?.statusTextVi ? "var(--confirm-txt)" : "",
              }}
            >
              {label?.detail}
            </Typography>

            <Typography sx={{ fontSize: "10px" }}>{dayjs(label?.createdAt).format("DD-MM-YYYY") + " - " + dayjs(label?.createdAt).format("HH:mm")}</Typography>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};
